// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportPartGridVerticalHorizontalActions.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import { compose, withState, withHandlers, withProps } from 'recompose';
import { isObservableArray } from 'mobx';
import constants, {
  DEFAULT_CELL_STYLE,
  EMPTY_CELL_BACKGROUND_COLOR,
  GRID_STYLES
} from 'logic/data/Izenda.ReportPartGridConstants';
import { CUSTOM_FORMAT_PREFIX } from 'logic/data/Izenda.ReportPartFieldConstants';
import reportPartUtils from 'share/utils/Izenda.ReportPartUtils';
import reportPartGridSetting from './Izenda.ReportPartGridSetting';
import stylingUtil from './Izenda.ReportPartGridStylingUtil';
import BrowserUtils from 'share/utils/Izenda.BrowserUtils';
import {
  assign,
  defaults,
  isNil,
  isUndefined,
  get,
  take,
  flow,
  fill,
  placeholder,
  reduce,
  map,
  max,
  isEmpty,
  compact,
  omit,
  getOr,
  find,
  flatten,
  concat,
  chunk,
  some,
  forEach,
  maxBy,
  curry
} from 'lodash/fp';
import { DEFAULT_VALUE_PROPERTIES } from 'logic/data/Izenda.ReportPartGridConstants';
import AppGlobal from 'Izenda.AppGlobal';
const DEFAULT_VERTICAL_ALIGNMENT = DEFAULT_VALUE_PROPERTIES.VERTICAL_ALIGNMENT;
/**
 * render cell content base on value and style
 * @param {Object} props
 * @param {Array} header
 * @param {Array} dataRow
 * @param {string} columnKey
 * @param {int} rowIndex
 * @param {Object} column
 * @param {int} colIndex
 */
const fnGetCellContentStyle = (
  props: any,
  header: any,
  dataRow: any,
  columnKey: any,
  rowIndex: number,
  column: any,
  colIndex: number
): any => {
  const reportPart = props.reportPart;
  const properties = reportPart.getReportPartProperties();
  const { columns } = properties;
  const columnProps = {
    wordWrap: columns.wordWrap,
    alignment: columns.alignment
  };
  const isHorizontal = properties.generalInfo.gridStyle === constants.GRID_STYLES.HORIZONTAL.value ? 1 : 0;

  if (isHorizontal && colIndex === 0) {
    var cellStyle = defaults(DEFAULT_CELL_STYLE, getHeaderCellStyle(props, header, dataRow));
    return cellStyle;
  }
  return defaults(
    DEFAULT_CELL_STYLE,
    getCellContentStyle(header, dataRow, isHorizontal, rowIndex, colIndex, columnProps)
  );
};

/**
 * Get color setting for Grid
 * @param {Object} header
 * @param {Array} dataRow
 * @param {boolean} isHorizontal
 * @param {int} rowIndex
 * @param {int} colIndex
 */
const getColorSettingForGrid = (
  header: any,
  dataRow: any,
  isHorizontal: boolean,
  rowIndex: number,
  colIndex: number
): any => {
  const columnHeader = isHorizontal ? dataRow : header;
  // set background & text color of cell follow

  const original_columnKey = CUSTOM_FORMAT_PREFIX + header.columnKey;
  const cell = !isUndefined(dataRow[original_columnKey])
    ? dataRow[original_columnKey]
    : !isUndefined(dataRow[header.columnKey])
      ? dataRow[header.columnKey]
      : dataRow[colIndex];

  let cellData = cell && !isNil(cell.data) ? (!isNil(cell.originalData) ? cell.originalData : cell.data) : cell;
  const element = columnHeader.element;
  const dataSource = {
    element: {
      columnName: isHorizontal ? dataRow.columnName : header.columnKey,
      reportPartElm: element
    },
    record: isHorizontal ? dataRow[colIndex] : dataRow
  };
  cellData = String.commonStringValue(cellData, AppGlobal.advancedSettings.other.convertNullToEmptyStringValue);

  const backgroundColor = reportPartUtils.getConfigDataByRange(
    cellData,
    element.properties.dataFormattings.color.cellColor,
    dataSource
  );
  const textColor = reportPartUtils.getConfigDataByRange(
    cellData,
    element.properties.dataFormattings.color.textColor,
    dataSource
  );

  return { backgroundColor, textColor };
};

/**
 * Get cell content style
 * @param {Object} header
 * @param {Array} dataRow
 * @param {boolean} isHorizontal
 * @param {int} rowIndex
 * @param {int} colIndex
 * @param {Object} columnProps
 */
const getCellContentStyle = (
  header: any,
  dataRow: any,
  isHorizontal: boolean,
  rowIndex: number,
  colIndex: number,
  columnProps: any
): any => {
  const buildStyle = item => {
    if (!item) {
      return {};
    }
    const { wordWrap, alignment } = columnProps;

    // Only check wordWrap in Report Part Properties if in FieldProperties, wordWrap is null
    const isWordWraped = isNil(item.wordWrap) ? wordWrap : item.wordWrap;
    const textAlign = isNil(item.alignment) ? alignment : item.alignment;
    const { backgroundColor, textColor } = getColorSettingForGrid(header, dataRow, isHorizontal, rowIndex, colIndex);

    // Get Conditional Formatting Styles for record/row
    const record = isHorizontal ? dataRow[colIndex] : dataRow;
    const columnName = isHorizontal ? dataRow.columnName : header.columnKey;
    const conditionalFormatting = getOr([], 'conditionalFormatting', record);
    const conditionalFormattingStyles = reportPartUtils.getCellConditionalFormattingValue(columnName, conditionalFormatting);

    return defaults(DEFAULT_CELL_STYLE, {
      fontFamily: get('fontFamily.value', conditionalFormattingStyles) || item.fontFamily,
      fontSize: (get('fontSize.value', conditionalFormattingStyles) || item.fontSize) + 'px',
      fontWeight: get('bold.value', conditionalFormattingStyles) || item.bold ? 'bold' : 'normal',
      textAlign: textAlign || 'none',
      textDecoration: get('underline.value', conditionalFormattingStyles) || item.underline ? 'underline' : 'none',
      fontStyle: get('italic.value', conditionalFormattingStyles) || item.italic ? 'italic' : 'normal',
      color: textColor || get('fontColor.value', conditionalFormattingStyles) || '',
      backgroundColor: backgroundColor || get('bgColor.value', conditionalFormattingStyles) || '',
      wordWrap: isWordWraped ? 'break-word' : 'normal',
      whiteSpace: isWordWraped ? 'pre-line' : 'pre'
    });
  };

  return buildStyle(get('cellStyle', dataRow) || header.cellStyle);
};

/**
 * Render cell content base on value and style
 * @method getHeaderCellStyle
 * @param  {[type]}            dataRow        [record for this dataRow]
 * @param  {int}  rowIndex  Row index of record
 * @return {React.element}                    [cell content]
 */
const getHeaderCellStyle = (props: any, header: any, dataRow: any) => {
  const buildStyle = item => {
    if (!item) {
      return {};
    }

    return defaults(DEFAULT_CELL_STYLE, {
      fontFamily: item.fontFamily,
      fontSize: item.fontSize + 'px',
      fontWeight: item.bold ? 'bold' : 'normal',
      textAlign: item.alignment || 'none',
      textDecoration: item.underline ? 'underline' : 'none',
      fontStyle: item.italic ? 'italic' : 'normal',
      color: item.textColor,
      backgroundColor: item.backgroundColor,
      wordWrap: item.wordWrap ? 'break-word' : 'normal',
      whiteSpace: item.wordWrap ? 'pre-line' : 'pre'
    });
  };

  return buildStyle(get('style.header', dataRow) || header.style);
};

/**
 * Generate header data for vertical grid
 * @param {Object} props
 * @param {any[]} records Data records for grid.
 * @param {boolean} exporting
 * @return {json} object contain header and records for vertical-grid
 */
const generateHeaderDataForVertical = (props: any, records: any[], exporting: boolean): any => {
  const {
    isPreview,
    reportPart,
    reportPart: {
      previewData,
      previewDataFieldsMapping,
      reportPartContent: { columns, properties: reportPartProperties }
    }
  } = props;

  const { columns: columnSetting, rows: rowSetting } = reportPartProperties;
  let headers = [];

  let newRecords = isObservableArray(records) ? records.peek() : records;

  // Max-length to show number of row
  newRecords = isPreview ? take(constants.RECORDS_MAX_LENGTH, newRecords) : newRecords;

  const { headers: reportPartHeaderProperties } = reportPart.getReportPartProperties();

  let subTotalRecord = {
    className: 'sub-total',
    isSubTotal: true
  };
  let haveSubTotal = false;

  const maxHeaderHeight = flow(
    map(get('properties.headerFormating.height')),
    max
  )(columns.visibleElement);

  if (!isEmpty(columns.visibleElement)) {
    // Build header from selected columns

    headers = compact(
      map(element => {
        const {
          properties: elementProperties,
          field: { fieldNameAlias, fieldId }
        } = element;
        const label = getOr('', 'dataFormattings.subTotal.label', elementProperties);

        // get field mapping
        const fieldsMapping = find({ fieldId, fieldNameAlias }, previewDataFieldsMapping);

        if (fieldsMapping) {
          const style = elementProperties.getHeaderCellStyle(reportPartHeaderProperties);
          const cellStyle = elementProperties.getUIStyle();

          style.height = maxHeaderHeight || rowSetting.height;
          cellStyle.height = rowSetting.height;
          const columnWidth = elementProperties.dataFormattings.width.value || columnSetting.width.value;

          // check sub-total
          const subTotalMapping = find({ fieldNameAlias: fieldNameAlias }, previewData.subTotalMapping);

          if (subTotalMapping) {
            const subTotalFormat = get('dataFormattings.subTotal.format', elementProperties);
            const subTotalDataType = get('dataFormattings.subTotal.dataType', elementProperties);

            if (!isNil(records[0][subTotalMapping.columnName])) {
              subTotalRecord[fieldNameAlias] = {
                data: records[0][subTotalMapping.columnName],
                subTotalFormat,
                title: label,
                subTotalDataType
              };
            }
            haveSubTotal = true;
          } else {
            subTotalRecord[fieldNameAlias] = {
              data: '',
              title: ''
            };
          }

          return {
            fieldId: fieldsMapping.fieldId,
            columnKey: props.useFieldNameAlias ? fieldNameAlias : fieldsMapping.columnName,
            getTitle: props.renderHeader(columns, element, fieldNameAlias),
            width: columnWidth,
            style: style,
            cellStyle: cellStyle,
            element: element
          };
        }
        return null;
      }, columns.visibleElement)
    );
    // push subtotal into datasource record of grid
    haveSubTotal && (newRecords = concat(newRecords, subTotalRecord));
  }

  const pivotColumnsPerExportedPage = reportPartProperties.view.pivotColumnsPerExportedPage;

  if (exporting && pivotColumnsPerExportedPage) {
    headers = chunk(pivotColumnsPerExportedPage, headers);
  } else {
    headers = [headers];
  }

  return map(page => {
    return {
      headers: processColumnGroupVertical(page),
      records: newRecords
    };
  })(headers);
};

/**
 * Generate header data for vertical grid with logical separator.
 * @param props
 * @param exporting
 * @returns {Array|*[]}
 */
const generateHeaderDataForVerticalLogical = (props: any, exporting: boolean) => {
  const { records: recordGroups } = props;
  const headerList = flow(
    // get records data
    map(x => x.data || x),
    // calculate header for each records collection
    map(curry(generateHeaderDataForVertical)(props, placeholder, exporting)),
    flatten
  )(recordGroups);

  if (!headerList.length)
    return [];

  // combine headers collection altogether
  const result = reduce(
    (curr, next) => ({
      headers: curr.headers,
      records: concat(curr.records, next.records)
    }),
    { headers: headerList[0].headers, records: [] },
    headerList);
  return [result];
};

/**
 * Process Column Group Vertical
 * @param {Object} headers
 */
const processColumnGroupVertical = (headers: any) => {
  var haveColumnGroup = some('element.properties.headerFormating.columnGroup', headers);

  if (!haveColumnGroup) {
    return headers;
  }

  let lastGroup = null;
  let listGroup = [];

  forEach(header => {
    const columnGroup = get('element.properties.headerFormating.columnGroup', header);

    if (lastGroup && lastGroup.title === columnGroup) {
      lastGroup.child.push(header);
    } else {
      lastGroup = {
        title: columnGroup,
        isColumnGroup: true,
        child: [header]
      };
      listGroup.push(lastGroup);
    }
  }, headers);

  return listGroup;
};

/**
 * Generate header data for horizontal grid with logical separator.
 * @param {Object} props
 * @param {boolean} exporting
 * @return {json} object contain header and records for horizontal-grid
 */
const generateHeaderDataForHorizontalLogical = (props, exporting): any => {
  const { records: recordGroups } = props;

  let headerList = recordGroups.map(x => x.data || x);
  headerList = headerList.map((x, i) => generateHeaderDataForHorizontal(props, x, exporting, i===0));
  headerList = flatten(headerList);

  if (!headerList.length)
    return [];

  // combine headers collection altogether
  const recordsLength = headerList[0].records.length;
  let initialArray = fill(0, recordsLength, [], new Array(recordsLength));
  let result = reduce(
    (curr, next) => {
      const res = {
        headers: concat(curr.headers, next.headers),
        records: curr.records.map((recordsArray, i) => concat(recordsArray, next.records[i]))
      };
      return res;
    },
    {
      headers: [],
      records: initialArray
    },
    headerList);

  // apply
  result.records.forEach((x, i) => {
    const original = headerList[0].records[i];
    x.style = original.style;
    x.cellStyle = original.cellStyle;
    x.element = original.element;
    x.columnName = original.columnName;
  });

  result = assign(result, omit(['headers', 'records'], headerList[0]));

  return [result];
};

/**
 * Generate header data for horizontal grid.
 * @param {Object} props
 * @param {any[]} records Records.
 * @param {boolean} exporting
 * @param {boolean} appendHeader If false - header won't be generated.
 * @return {json} object contain header and records for horizontal-grid
 */
const generateHeaderDataForHorizontal = (props: any, records: any[], exporting: boolean, appendHeader = true): any => {
  const {
    reportPart,
    reportPart: {
      previewData,
      previewDataFieldsMapping,
      reportPartContent: { rows }
    }
  } = props;
  let pageRecords = null;
  const properties = reportPart.getReportPartProperties();
  const { columns: columnSetting, headers: reportPartHeaderProperties } = properties;

  const maxWidth = flow(
    map('properties.dataFormattings.width'),
    maxBy('value')
  )(rows.visibleElement);

  const configureWidth = {
    width: get('value', maxWidth) || columnSetting.width.value
  };

  const pivotColumnsPerExportedPage = properties.view.pivotColumnsPerExportedPage || 0;

  if (exporting && pivotColumnsPerExportedPage) {
    // make original array value into smaller chunk based on setting.
    pageRecords = chunk(pivotColumnsPerExportedPage, records.slice());
  } else if (props.isPreview) {
    // in report designer preview mode only have one chunk with maximum 50 records
    pageRecords = [take(constants.RECORDS_MAX_LENGTH, records.slice())];
  } else {
    // normal case only have 1 chunk with all records.
    pageRecords = [records.slice()];
  }

  const lastPage = pageRecords.length - 1;

  // each chunk will render 1 table.
  return pageRecords.map((page, index) => {
    let headers = [];
    let dataSource = [];
    let haveSubtotalMapping = false;
    let rowSpan;

    if (!isEmpty(rows.visibleElement)) {
      if (appendHeader)
        headers.push(configureWidth);

      // Max-length to show number of column
      const recordsMaxLength = page.length;

      for (var i = 0; i < recordsMaxLength; i++) {
        headers.push(configureWidth);
      }

      const haveColumnGroup = some('properties.headerFormating.columnGroup', rows.visibleElement);

      let lastGroup = null;

      if (haveColumnGroup) {
        rowSpan = assign(configureWidth, { data: [] });
      }

      // Build data from selected columns
      rows.visibleElement.forEach((element) => {
        const {
          field: { fieldId, fieldNameAlias }
        } = element;
        const fieldsMapping = find({ fieldId, fieldNameAlias }, previewDataFieldsMapping);

        if (isNil(fieldsMapping)) {
          return;
        }

        const columnGroup = get('properties.headerFormating.columnGroup', element);

        if (haveColumnGroup) {
          if (lastGroup && lastGroup.title === columnGroup) {
            lastGroup.size++;
          } else {
            lastGroup = {
              title: columnGroup,
              size: 1
            };
            rowSpan.data.push(lastGroup);
          }
        }

        // build data for each rows
        let row = [];

        if (appendHeader)
          row.push({
            className: 'header-row',
            data: element.field.fieldNameAlias
          });

        forEach(record => {
          row.push({
            ...record,
            data: record[fieldsMapping.columnName],
            originalData: record[CUSTOM_FORMAT_PREFIX + fieldsMapping.columnName]
          });
        }, page);

        // sub-total
        const label = getOr('', 'properties.dataFormattings.subTotal.label', element);
        const subTotalMapping = find({ subTotalForColumnName: fieldsMapping.columnName }, previewData.subTotalMapping);

        if (!isEmpty(subTotalMapping) && index === lastPage) {
          if (!haveSubtotalMapping) {
            headers.push(configureWidth);
            haveSubtotalMapping = !haveSubtotalMapping;
          }
          let rowDataSubtotal = records[0][subTotalMapping.columnName];
          const subTotalDataType = get('properties.dataFormattings.subTotal.dataType', element);

          row.push({
            className: 'sub-total',
            data: rowDataSubtotal,
            subTotalFormat: get('properties.dataFormattings.subTotal.format', element),
            title: label,
            subTotalDataType: subTotalDataType
          });
        } else if (isEmpty(subTotalMapping) && index === lastPage) {
          row.push({
            data: '',
            title: ''
          });
        }

        row.style = {};
        row.style.header = element.properties.getHeaderCellStyle(reportPartHeaderProperties);
        row.cellStyle = element.properties.getUIStyle();
        row.element = element;
        row.columnName = props.useFieldNameAlias ? fieldNameAlias : fieldsMapping.columnName;

        dataSource.push(row);
      });
    }

    return {
      headers: headers,
      records: dataSource,
      rowSpan: rowSpan
    };
  });
};

/**
 * Generate header data
 * @param {Object} props
 * @return {json} object contain header and records
 */
const generateHeaderData = (props: any, e: any): any => {
  const {isLogicalSeparator} = props;
  const gridStyle = props.reportPart.reportPartContent.properties.generalInfo.gridStyle;
  const exporting = props.printingMode;

  // Logical separator get not collection of records, but collection of collections.
  // So we need to merge results into the one table:
  if (isLogicalSeparator) {
    return gridStyle === constants.GRID_STYLES.HORIZONTAL.value
      ? generateHeaderDataForHorizontalLogical(props, exporting)
      : generateHeaderDataForVerticalLogical(props, exporting);
  }

  return gridStyle === constants.GRID_STYLES.HORIZONTAL.value
    ? generateHeaderDataForHorizontal(props, props.records, exporting)
    : generateHeaderDataForVertical(props, props.records, exporting);
};

/**
 * Get Column Group Cell Style
 * @param {Object} props
 * @param {Object} totalGroup
 * @param {Object} rowSpan
 * @param {int} rowIndex
 */
const getColumnGroupCellStyle = (
  props: any,
  totalGroup: any,
  rowSpan: any,
  rowIndex: number,
  placeHolder: any
): any => {
  const {
    reportPartContent: { properties }
  } = props.reportPart;
  const isHorizontal = properties.generalInfo.gridStyle === GRID_STYLES.HORIZONTAL.value;

  const reportPartHeaderProperties = properties.headers;
  const border = properties.table && properties.table.border;
  let borderStyle = {};

  // Border mid vertical
  if (border.midVer && rowIndex !== totalGroup - 1 && !isHorizontal) {
    borderStyle = assign(borderStyle, reportPartGridSetting.getBorderStyle('Right', border.midVer));
  }

  // Border mid Horizontal
  if (border.midHor && isHorizontal && rowIndex !== totalGroup - 1) {
    borderStyle = assign(borderStyle, reportPartGridSetting.getBorderStyle('Bottom', border.midHor));
  }

  if (!rowSpan.title) {
    return defaults({ background: EMPTY_CELL_BACKGROUND_COLOR }, borderStyle);
  }

  if (border.top && (rowIndex === 0 || !isHorizontal)) {
    borderStyle = assign(borderStyle, reportPartGridSetting.getBorderStyle('Top', border.top));
  }
  // Border bottom
  if (border.bottom && (isHorizontal && rowIndex === totalGroup - 1)) {
    borderStyle = assign(borderStyle, reportPartGridSetting.getBorderStyle('Bottom', border.bottom));
  }

  // Border left
  if (border.left && (isHorizontal || rowIndex === 0)) {
    borderStyle = assign(borderStyle, reportPartGridSetting.getBorderStyle('Left', border.left));
  }

  // Border right
  if (border.right && (!isHorizontal && rowIndex === totalGroup - 1)) {
    borderStyle = assign(borderStyle, reportPartGridSetting.getBorderStyle('Right', border.right));
  }

  const backGroundColor = get('font.backgroundColor', reportPartHeaderProperties);
  const verticalAlign = get('verticalAlignment', reportPartHeaderProperties);
  const wordWrapStyle = get('wordWrap', reportPartHeaderProperties)
    ? {
        wordWrap: 'break-word',
        whiteSpace: 'pre-line'
      }
    : {
        wordWrap: 'normal',
        whiteSpace: 'pre'
      };

  return defaults(defaults({ background: backGroundColor, verticalAlign }, wordWrapStyle), borderStyle);
};

/**
 * Get Column group cell content style
 * @param {Object} props [report part props]
 * @param {Object} rowSpan [row span config]
 * @return {Object}          [style of cell]
 */
const getColumnGroupCellContentStyle = (props: any, rowSpan: any, e: any): any => {
  if (!rowSpan.title) {
    return {};
  }
  return stylingUtil.createStyleObject(props.reportPart.reportPartContent.properties.getHeaderCellStyle());
};

/**
 * Get Default Cell Style from Empty Grid
 * @param {Object} border [report part props table border]
 * @return {Object}          [style of cell]
 */

const getEmptyBorderCellStyle = (border: any): any => {
  if(!border.top.color && !border.right.color && !border.bottom.color && !border.midVer.color && !border.left.color && !border.midHor.color) {
    return {
      top: {
        color: null,
        dashStyle: null,
        thinkness: null
      },
      right: {
        color: null,
        dashStyle: null,
        thinkness: null
      },
      bottom: {
        color: null,
        dashStyle: null,
        thinkness: null
      },
      midVer: {
        color: '#dbdcdd',
        dashStyle: 'solid',
        thinkness: 1
      },
      left: {
        color: null,
        dashStyle: null,
        thinkness: null
      },
      midHor: {
        color: '#dbdcdd',
        dashStyle: 'solid',
        thinkness: 1
      }
    }
  }
  return border;
};

/**
 * Get cell style of cell
 * @param  {Object} dataRow  [data row]
 * @param  {Number} rowIndex [index of row]
 * @return {Object}          [style of cell]
 */
const getCellStyle = (
  props: any,
  header: any,
  numberOfRecord: number,
  totalColumns: number,
  dataRow: any,
  rowIndex: number,
  colIndex: number
) => {
  const {
    reportPart,
    reportPart: { reportPartContent },
    embededReportPart
  } = props;
  const isMobile = BrowserUtils.isMobileDevice();
  const properties = reportPart.getReportPartProperties();
  const { rows, columns, table, isHeaderStickHorizontal } = properties;
  const isHorizontal = properties.generalInfo.gridStyle === GRID_STYLES.HORIZONTAL.value ? 1 : 0;
  const haveColumnGroup = some('properties.headerFormating.columnGroup', reportPartContent.rows.visibleElement);
  const hasLeftStyle = !isMobile && !embededReportPart && haveColumnGroup && colIndex === 0 && isHeaderStickHorizontal;
  // Cell background color
  const useAlternateColorRow = rows && rows.alterBackgroundColor ? 1 : 0;
  const useAlternateColorColumn = columns && columns.alterBackgroundColor ? 1 : 0;
  const columnVerticalAlignment = columns.verticalAlignment;

  const backgroundColor =
    (rowIndex * useAlternateColorRow + (colIndex + isHorizontal) * useAlternateColorColumn) % 2 === 0
      ? table.backgroundColor
      : table.alternativeBackgroundColor;

  // Cell border style
  const border = getEmptyBorderCellStyle(table.border);
  let borderStyle = {};
  const getBorderStyle = reportPartGridSetting.getBorderStyle;

  // Border middle vertical
  if (border.midVer && colIndex !== totalColumns - 1) {
    borderStyle = assign(borderStyle, getBorderStyle('Right', border.midVer));
  }

  // Border middle horizontal
  if (border.midHor && rowIndex !== numberOfRecord - 1) {
    borderStyle = assign(borderStyle, getBorderStyle('Bottom', border.midHor));
  }

  // Border left
  if (border.left && colIndex === 0) {
    borderStyle = assign(borderStyle, getBorderStyle('Left', border.left));
  }

  // Border right
  if (border.right && colIndex === totalColumns - 1) {
    borderStyle = assign(borderStyle, getBorderStyle('Right', border.right));
  }

  // Border top
  if (border.top && isNil(rowIndex)) {
    borderStyle = assign(borderStyle, getBorderStyle('Top', border.top));
  }

  if (border.top && isHorizontal && rowIndex === 0) {
    borderStyle = assign(borderStyle, getBorderStyle('Top', border.top));
  }

  // Border bottom
  if (border.bottom && rowIndex === numberOfRecord - 1) {
    borderStyle = assign(borderStyle, getBorderStyle('Bottom', border.bottom));
  }

  const headerStyle = isHorizontal && colIndex === 0 && dataRow ? dataRow.style.header : header.style;

  let verticalAlign = '';

  if (!isHorizontal) {
    verticalAlign = isNil(dataRow)
      ? (header.element && header.element.properties.headerFormating.verticalAlignment) ||
        header.style.verticalAlignment
      : (header.element && header.element.properties.dataFormattings.verticalAlignment) || columnVerticalAlignment;
  } else {
    verticalAlign = isNil(dataRow)
      ? DEFAULT_VERTICAL_ALIGNMENT
      : colIndex === 0
      ? (dataRow.element && dataRow.element.properties.headerFormating.verticalAlignment) ||
        dataRow.style.header.verticalAlignment
      : (dataRow.element && dataRow.element.properties.dataFormattings.verticalAlignment) || columnVerticalAlignment;
  }

  return defaults(borderStyle, {
    backgroundColor:
      isNil(dataRow) || (isHorizontal && colIndex === 0)
        ? headerStyle
          ? headerStyle.backgroundColor
          : EMPTY_CELL_BACKGROUND_COLOR
        : backgroundColor,
    verticalAlign,
    wordWrap: columns.wordWrap,
    left: hasLeftStyle && header.width
  });
};

export default compose(
  withState('isLoading', 'updateLoading', true),
  withHandlers({
    generateHeaderData: curry(generateHeaderData),
    fnGetCellContentStyle: curry(fnGetCellContentStyle),
    getHeaderCellStyle: curry(getHeaderCellStyle),
    getCellStyle: curry(getCellStyle),
    getColumnGroupCellStyle: curry(getColumnGroupCellStyle),
    getColumnGroupCellContentStyle: curry(getColumnGroupCellContentStyle)
  }),
  withProps(props => {
    const { embededReportPart } = props;
    const isMobile = BrowserUtils.isMobileDevice();
    return {
      getStickyValue(value) {
        return embededReportPart || isMobile ? false : value;
      }
    };
  })
);



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/ReportBody/ReportPartType/ReportPartGrid/Izenda.ReportPartGridVerticalHorizontalActions.js